import { useState } from "react";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { ComponentSize } from "./types";
import { Spinner } from "./Spinner";
import { Pagination } from "./Pagination";

const DEFAULT_PAGE_SIZE = 20;

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;

export const Table = <T,>({
    data,
    columns,
    size = ComponentSize.MD,
    pagination = false,
    isLoading = false,
    paginationProps,
    sortingProps,
}: TableProps<T>) => {
    const [internalSorting, setSorting] = useState<SortingState>([]);
    // A caller that keeps the sort elsewhere (the URL) passes it as `sorting`,
    // so the headers follow it rather than the last click.
    const sorting = sortingProps?.sorting ?? internalSorting;

    const tableClasses = classNames(
        "table",
        size === ComponentSize.XS ? "table-xs" : "",
        size === ComponentSize.SM ? "table-sm" : "",
        size === ComponentSize.MD ? "table-md" : "",
        size === ComponentSize.LG ? "table-lg" : "",
        size === ComponentSize.XL ? "table-xl" : "",
        "text-neutral",
        "mt-5",
    );

    const handleSortingChange = (
        updaterOrValue: SortingState | ((old: SortingState) => SortingState),
    ) => {
        const newSorting =
            typeof updaterOrValue === "function"
                ? updaterOrValue(sorting)
                : updaterOrValue;

        if (!sortingProps?.sorting) setSorting(newSorting);

        if (sortingProps?.onSortingChange) {
            if (newSorting.length > 0) {
                sortingProps.onSortingChange(
                    newSorting[0].id,
                    newSorting[0].desc ? "desc" : "asc",
                );
            } else {
                // sorting cleared
                sortingProps.onSortingChange(null, null);
            }
        }
    };

    // TanStack Table's useReactTable returns memoization-unsafe functions.
    // React Compiler skips memoization for this hook by default; the warning
    // is informational, not a bug. Suppress at the call site.
    // eslint-disable-next-line react-hooks/incompatible-library
    const reactTable = useReactTable({
        data,
        columns,
        state: {
            sorting,
            ...(pagination && paginationProps
                ? {}
                : {
                    pagination: {
                        pageIndex: 0,
                        pageSize: data.length || DEFAULT_PAGE_SIZE,
                    },
                }),
        },
        onSortingChange: handleSortingChange,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        manualPagination: pagination && !!paginationProps,
        manualSorting: !!sortingProps?.onSortingChange,
        enableSortingRemoval: sortingProps?.enableSortingRemoval ?? true,
        // onSortingChange reports one column, so a shift-click sorts by the
        // clicked column alone instead of adding a second sort.
        enableMultiSort: false,
    });

    return (
        <div className="overflow-x-auto">
            <table className={tableClasses}>
                <thead>
                    <tr className="text-neutral border-y-2 border-gray-200">
                        {reactTable.getHeaderGroups().map((hg) =>
                            hg.headers.map((header) => {
                                const canSort = header.column.getCanSort();
                                const isSorted = header.column.getIsSorted();

                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={{
                                            width: `${header.getSize()}px`,
                                        }}
                                        // Only the sorted header carries aria-sort
                                        // (ARIA APG sortable table). canSort: a
                                        // controlled sort can name a fixed column
                                        // (Inventory ?sortBy=Price), which the
                                        // request does not sort by.
                                        aria-sort={
                                            canSort && isSorted
                                                ? ARIA_SORT[isSorted]
                                                : undefined
                                        }
                                    >
                                        {header.isPlaceholder ? null : canSort ? (
                                            // A button, so the sort is reachable from the keyboard.
                                            <button
                                                type="button"
                                                className="w-full text-left cursor-pointer select-none"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {/* TODO: change style. aria-sort carries the state. */}
                                                <span aria-hidden="true">
                                                    {{
                                                        asc: " ⬆️",
                                                        desc: " ⬇️",
                                                    }[isSorted as string] ?? null}
                                                </span>
                                            </button>
                                        ) : (
                                            flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )
                                        )}
                                    </th>
                                );
                            }),
                        )}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-500">
                    {isLoading ? (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="text-center py-8"
                            >
                                <Spinner size="lg" />
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="text-center py-8 text-gray-500"
                            >
                                No data to display
                            </td>
                        </tr>
                    ) : (
                        reactTable.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className="border-b border-gray-200 even:bg-gray-100 odd:bg-white"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {pagination && paginationProps && !isLoading && data.length > 0 && (
                <Pagination
                    currentPage={paginationProps.currentPage}
                    totalPages={paginationProps.totalPages}
                    pageSize={paginationProps.pageSize}
                    pageSizeOptions={paginationProps.pageSizeOptions}
                    count={paginationProps.count}
                    allCount={paginationProps.allCount}
                    // The page buttons get a third of the footer; a longer
                    // window runs under the centred page-size select.
                    maxVisible={7}
                    onPageChange={paginationProps.onPageChange}
                    onPageSizeChange={paginationProps.onPageSizeChange}
                    hasPreviousPage={paginationProps.hasPreviousPage}
                    hasNextPage={paginationProps.hasNextPage}
                />
            )}
        </div>
    );
};

interface TablePaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    pageSizeOptions: number[];
    // Rows matching the filter, and (once the API sends it) rows before it.
    count: number;
    allCount?: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

interface TableSortingProps {
    onSortingChange: (columnId: string | null, direction: 'asc' | 'desc' | null) => void;
    // Controlled sort: when set, it is the sort the headers show.
    sorting?: SortingState;
    // false: a sorted column's next click flips the direction instead of
    // clearing the sort (for a list that is always sorted).
    enableSortingRemoval?: boolean;
}

interface TableProps<T> {
    data: T[];
    columns: Array<ColumnDef<T, unknown>>;
    size?: ComponentSize;
    pagination?: boolean;
    isLoading?: boolean;
    paginationProps?: TablePaginationProps;
    sortingProps?: TableSortingProps;
}

export default Table;
